import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MapPin, Calendar, Map as MapIcon, LocateFixed, X, Globe } from "lucide-react";
import MatchCard from "@/components/MatchCard";
import api from "@/services/api";
import { toast } from "sonner";
import { haversineKm, loadCachedPosition, saveCachedPosition } from "@/services/geo";
import { useSettings } from "@/context/SettingsContext";

// Default radius (km) for "near me" filtering; overridden by dynamic settings.
const RADIUS_KM_DEFAULT = 20;

// localStorage key for persisting the last validated city pick across sessions.
const LAST_CITY_KEY = "matchup:last_searched_city";

const loadLastSearchedCity = () => {
  try {
    const raw = localStorage.getItem(LAST_CITY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.city === "string" && typeof parsed.lat === "number" && typeof parsed.lng === "number") {
      return parsed;
    }
  } catch (_) {
    // Corrupt/legacy entry — purge silently.
    try { localStorage.removeItem(LAST_CITY_KEY); } catch (_) {}
  }
  return null;
};

const saveLastSearchedCity = (hit) => {
  try {
    localStorage.setItem(
      LAST_CITY_KEY,
      JSON.stringify({ city: hit.city, lat: hit.lat, lng: hit.lng }),
    );
  } catch (_) { /* localStorage may be unavailable (private mode, quota) */ }
};

const clearLastSearchedCity = () => {
  try { localStorage.removeItem(LAST_CITY_KEY); } catch (_) {}
};

export default function HomePage() {
  const navigate = useNavigate();
  const settings = useSettings();
  const RADIUS_KM = settings.search_radius_km || RADIUS_KM_DEFAULT;
  const [matches, setMatches] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  // Hydrate the city filter from localStorage so the user sees their last
  // searched city instead of an empty input on each session.
  const initialCity = loadLastSearchedCity();
  const [cityFilter, setCityFilter] = useState(initialCity?.city || "");
  const [dateFilter, setDateFilter] = useState("");
  const [gpsPosition, setGpsPosition] = useState(() => loadCachedPosition());
  // When the user types a city manually we resolve its coords and use them as origin.
  const [manualCityCoords, setManualCityCoords] = useState(initialCity);
  const [isLocating, setIsLocating] = useState(false);
  // When true, the user explicitly asked to ignore the 20km radius (fallback "voir partout").
  const [ignoreRadius, setIgnoreRadius] = useState(false);

  const gpsAttemptedRef = useRef(false);
  const cityDebounceRef = useRef(null);
  const userTypingRef = useRef(false); // protects user typing from late GPS callback

  const loadMatches = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.getMatches("open");
      setMatches(data);
    } catch (error) {
      toast.error("Erreur lors du chargement des matchs");
    } finally {
      setIsLoading(false);
    }
  }, []);

  // GPS auto-trigger: fires ONCE at mount (not on every focus/click) so it
  // never overwrites what the user is typing.
  const requestGps = useCallback((opts = { silent: true }) => {
    if (!navigator.geolocation) return;
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude: lat, longitude: lng } = pos.coords;
        try {
          const res = await api.geoReverse(lat, lng);
          const city = res?.city || "";
          setGpsPosition({ lat, lng, city });
          saveCachedPosition(lat, lng, city);
          // Only auto-fill the city if the user hasn't started typing.
          if (city && !userTypingRef.current && !cityFilter) {
            setCityFilter(city);
          }
          if (!opts.silent) {
            toast.success(city ? `Position : ${city}` : "Position detectee");
          }
        } catch (_) {
          setGpsPosition({ lat, lng, city: "" });
          saveCachedPosition(lat, lng, "");
        } finally {
          setIsLocating(false);
        }
      },
      () => {
        setIsLocating(false);
        if (!opts.silent) toast.error("Position non autorisee");
      },
      { timeout: 6000, enableHighAccuracy: false, maximumAge: 5 * 60 * 1000 },
    );
  }, [cityFilter]);

  useEffect(() => {
    loadMatches();
    if (!gpsAttemptedRef.current) {
      gpsAttemptedRef.current = true;
      requestGps({ silent: true });
    }
    const id = setInterval(() => {
      if (!document.hidden) {
        api.getMatches("open").then(setMatches).catch(() => {});
      }
    }, 30000);
    // React to global match-creation event from the BottomNav-triggered dialog.
    const onCreated = () => loadMatches();
    window.addEventListener("matchup:match-created", onCreated);
    return () => {
      clearInterval(id);
      window.removeEventListener("matchup:match-created", onCreated);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadMatches]);

  // When the user types a city manually, fetch up to 8 suggestions (debounced).
  // The user MUST pick one — typed text alone won't activate the city filter.
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);

  useEffect(() => {
    const q = cityFilter.trim();
    if (cityDebounceRef.current) clearTimeout(cityDebounceRef.current);
    if (!q) {
      setManualCityCoords(null);
      setIgnoreRadius(false);
      setCitySuggestions([]);
      setShowCitySuggestions(false);
      return;
    }
    // If the user has already validated a suggestion matching the current text, skip the API hit.
    if (manualCityCoords && manualCityCoords.city.toLowerCase() === q.toLowerCase()) {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
      return;
    }
    cityDebounceRef.current = setTimeout(async () => {
      try {
        const res = await api.geoForward(q, 8);
        const list = res?.suggestions || [];
        setCitySuggestions(list);
        setShowCitySuggestions(list.length > 0);
        // Until the user picks one explicitly, do not apply any coord-based filter.
        setManualCityCoords(null);
      } catch {
        setCitySuggestions([]);
        setShowCitySuggestions(false);
      }
    }, 280);
  }, [cityFilter, manualCityCoords]);

  const pickCitySuggestion = (hit) => {
    setCityFilter(hit.city);
    setManualCityCoords({ lat: hit.lat, lng: hit.lng, city: hit.city });
    setIgnoreRadius(false);
    setShowCitySuggestions(false);
    setCitySuggestions([]);
    saveLastSearchedCity(hit);
  };

  // Active origin label for the radius hint.
  const usingManualCity = Boolean(manualCityCoords);
  const hintOrigin = manualCityCoords || (gpsPosition?.lat != null ? gpsPosition : null);

  const filteredMatches = matches.filter((match) => {
    if (match.status && match.status !== "open") return false;
    // When the user has picked an official city, show ALL matches in that city
    // (either by name match OR by 20km radius around its coords) — manual search prevails.
    if (manualCityCoords) {
      const cityNorm = manualCityCoords.city.toLowerCase();
      const locNorm = (match.location || "").toLowerCase();
      const cityMatch = locNorm.includes(cityNorm);
      let coordMatch = false;
      if (match.coordinates && Array.isArray(match.coordinates)) {
        const [mLng, mLat] = match.coordinates;
        coordMatch = haversineKm(manualCityCoords.lat, manualCityCoords.lng, mLat, mLng) <= RADIUS_KM;
      }
      return cityMatch || coordMatch;
    }
    // No validated city: fall back to GPS radius filter (or no filter when ignored).
    if (!ignoreRadius && gpsPosition?.lat != null && match.coordinates && Array.isArray(match.coordinates)) {
      const [mLng, mLat] = match.coordinates;
      const d = haversineKm(gpsPosition.lat, gpsPosition.lng, mLat, mLng);
      if (d > RADIUS_KM) return false;
    }
    return true;
  });

  const handleClearCity = () => {
    userTypingRef.current = false;
    setCityFilter("");
    setManualCityCoords(null);
    setIgnoreRadius(false);
    clearLastSearchedCity();
  };

  const handleUseMyPosition = () => {
    userTypingRef.current = false;
    setManualCityCoords(null);
    setIgnoreRadius(false);
    setCityFilter("");
    clearLastSearchedCity();
    requestGps({ silent: false });
  };

  const inputStyle = {
    height: '48px',
    width: '100%',
    paddingLeft: '44px',
    paddingRight: '110px',
    borderRadius: '12px',
    backgroundColor: '#111111',
    border: '1px solid #333',
    color: '#ffffff',
    fontSize: '14px',
    outline: 'none',
  };

  return (
    <div data-testid="home-page" style={{ minHeight: '100vh', backgroundColor: '#000' }}>
      <header style={{ paddingTop: '32px', paddingBottom: '24px', textAlign: 'center' }}>
        <h1 className="font-sport uppercase" style={{ fontSize: '32px', fontWeight: 700, color: '#fff', letterSpacing: '0.01em' }}>
          MatchUp
        </h1>
        <p style={{ color: '#6b7280', marginTop: '8px', fontSize: '14px' }}>
          Trouve ton prochain match ⚽
        </p>
      </header>

      <section style={{ padding: '0 16px 16px 16px' }}>
        <button
          data-testid="find-match-cta"
          onClick={() => navigate('/find')}
          style={{
            width: '100%', height: '52px', borderRadius: '12px',
            background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
            border: 'none', color: '#fff', fontWeight: 700, fontSize: '14px',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
            cursor: 'pointer', touchAction: 'manipulation',
            boxShadow: '0 4px 16px rgba(34,197,94,0.25)', marginBottom: '12px',
          }}
        >
          <MapIcon size={18} /> Trouver un terrain & créer un match
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ position: 'relative', width: '100%' }}>
            <MapPin size={18} style={{
              position: 'absolute', left: '16px', top: '50%',
              transform: 'translateY(-50%)', color: '#22c55e', zIndex: 1,
            }} />
            <input
              data-testid="city-search-input"
              type="text"
              placeholder="Ville"
              value={cityFilter}
              onChange={(e) => {
                userTypingRef.current = true;
                setCityFilter(e.target.value);
                setIgnoreRadius(false);
              }}
              onFocus={() => citySuggestions.length > 0 && setShowCitySuggestions(true)}
              onBlur={() => setTimeout(() => setShowCitySuggestions(false), 150)}
              autoComplete="off"
              style={inputStyle}
              className="placeholder:text-gray-500 focus:ring-2 focus:ring-[#22c55e]"
            />

            {/* Right-aligned controls */}
            <div style={{
              position: 'absolute', right: '8px', top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}>
              {cityFilter ? (
                <button
                  data-testid="city-clear-btn"
                  onClick={handleClearCity}
                  aria-label="Effacer la ville"
                  style={{
                    width: 26, height: 26, borderRadius: 999,
                    background: '#1f1f1f', border: '1px solid #333',
                    color: '#9ca3af', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', cursor: 'pointer',
                  }}
                >
                  <X size={14} />
                </button>
              ) : null}

              <button
                data-testid="use-my-position-btn"
                onClick={handleUseMyPosition}
                aria-label="Utiliser ma position"
                style={{
                  height: 28, borderRadius: 999, padding: '0 10px',
                  background: 'rgba(34,197,94,0.12)',
                  border: '1px solid rgba(34,197,94,0.35)',
                  color: '#22c55e', fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center', gap: 4,
                  cursor: 'pointer',
                }}
              >
                {isLocating ? <LocateFixed size={12} className="animate-pulse" /> : <LocateFixed size={12} />}
                Ma position
              </button>
            </div>

            {showCitySuggestions && citySuggestions.length > 0 && (
              <ul
                data-testid="city-suggestions"
                style={{
                  position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
                  background: '#0a0a0a', border: '1px solid #1a1a1a', borderRadius: 12,
                  listStyle: 'none', padding: 4, margin: 0, zIndex: 1000,
                  maxHeight: 280, overflow: 'auto',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                }}
              >
                {citySuggestions.map((h) => (
                  <li
                    key={`${h.city}-${h.lat}`}
                    data-testid={`city-sugg-${h.city}`}
                    onMouseDown={(e) => { e.preventDefault(); pickCitySuggestion(h); }}
                    style={{
                      padding: '10px 12px', borderRadius: 8, cursor: 'pointer',
                      display: 'flex', alignItems: 'center', gap: 8, color: '#fff',
                      fontSize: 13,
                    }}
                    className="hover:bg-[#1a1a1a]"
                  >
                    <MapPin size={13} style={{ color: '#22c55e', flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{h.city}</span>
                    <span style={{ fontSize: 10, color: '#6b7280' }}>
                      {h.lat.toFixed(2)}, {h.lng.toFixed(2)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div style={{ position: 'relative', width: '100%' }}>
            <Calendar size={18} style={{
              position: 'absolute', left: '16px', top: '50%',
              transform: 'translateY(-50%)', color: '#22c55e', zIndex: 1,
            }} />
            <input
              data-testid="date-search-input"
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              style={{ ...inputStyle, paddingRight: '16px' }}
              className="focus:ring-2 focus:ring-[#22c55e]"
            />
          </div>
        </div>
      </section>

      <section style={{ padding: '0 16px 24px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <h2 className="font-heading" style={{ fontSize: '18px', fontWeight: 600, color: '#fff' }}>
            Matchs en Vedette
          </h2>
        </div>

        <p data-testid="radius-hint" style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
          {ignoreRadius
            ? "Tous les matchs disponibles partout en France"
            : usingManualCity
              ? `Tous les matchs a ${hintOrigin.city} (rayon ${RADIUS_KM} km autour de la ville)`
              : hintOrigin?.lat != null
                ? `Matchs dans un rayon de ${RADIUS_KM} km`
                : "Active la geolocalisation ou choisis une ville"}
        </p>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '48px 0' }}>
            <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid #333', borderTopColor: '#22c55e', borderRadius: '50%', margin: '0 auto' }} />
            <p style={{ color: '#6b7280', marginTop: '12px' }}>Chargement...</p>
          </div>
        ) : filteredMatches.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {filteredMatches.map((match) => (
              <MatchCard
                key={match.id}
                match={match}
                onDeleted={(id) => setMatches((prev) => prev.filter((m) => m.id !== id))}
                onDeleteError={loadMatches}
              />
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</div>
            <p data-testid="no-match-text" style={{ color: '#9ca3af', marginBottom: '20px' }}>{settings.text_no_match || "Aucun match ici"}</p>
            {!ignoreRadius && hintOrigin?.lat != null && (
              <button
                data-testid="see-all-france-btn"
                onClick={() => setIgnoreRadius(true)}
                style={{
                  minHeight: 44, padding: '0 16px', borderRadius: 12,
                  background: 'transparent', border: '1px solid #22c55e',
                  color: '#22c55e', fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', display: 'inline-flex',
                  alignItems: 'center', gap: 8,
                }}
              >
                <Globe size={14} />
                Voir les matchs partout en France
              </button>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
